import { useRef } from 'react';
import type { ReactNode } from 'react';
import { useTheme } from './context';
import type { ThemePref } from './theme';
import './ThemeToggle.css';

/** Decorative glyphs (aria-hidden — the accessible name is the segment's label). */
const SunIcon = (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <circle cx="8" cy="8" r="3" />
    <path
      strokeLinecap="round"
      d="M8 1.5v1.6M8 12.9v1.6M1.5 8h1.6M12.9 8h1.6M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M12.6 3.4l-1.1 1.1M4.5 11.5l-1.1 1.1"
    />
  </svg>
);

const MoonIcon = (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor">
    <path d="M13.4 10.5A5.6 5.6 0 015.5 2.6 5.6 5.6 0 108 13.6a5.6 5.6 0 005.4-3.1z" />
  </svg>
);

const SystemIcon = (
  <svg
    viewBox="0 0 16 16"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <circle cx="8" cy="8" r="5.5" />
    <path d="M8 2.5a5.5 5.5 0 010 11z" fill="currentColor" stroke="none" />
  </svg>
);

const OPTIONS: readonly { pref: ThemePref; label: string; icon: ReactNode }[] = [
  { pref: 'light', label: 'Light', icon: SunIcon },
  { pref: 'dark', label: 'Dark', icon: MoonIcon },
  { pref: 'system', label: 'System', icon: SystemIcon },
];

/**
 * The rail-footer appearance control (cue-forge-c6b): a 3-segment radiogroup
 * (Light / Dark / System). Selection follows focus with roving tabindex — the
 * same keyboard pattern as the Rail tablist — so a keyboard user arrows through
 * the options and the theme applies immediately. Native semantics (role=radio +
 * aria-checked) keep it screen-reader-legible; the icons are decorative.
 */
export function ThemeToggle() {
  const { pref, setPref } = useTheme();
  const refs = useRef(new Map<ThemePref, HTMLButtonElement>());

  function onKeyDown(e: React.KeyboardEvent) {
    const current = OPTIONS.findIndex((o) => o.pref === pref);
    let next: number;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % OPTIONS.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + OPTIONS.length) % OPTIONS.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = OPTIONS.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const opt = OPTIONS[next];
    if (!opt) return; // unreachable for a non-empty option list
    setPref(opt.pref);
    refs.current.get(opt.pref)?.focus();
  }

  return (
    <div
      className="cf-theme-toggle"
      role="radiogroup"
      aria-label="Appearance"
      onKeyDown={onKeyDown}
    >
      {OPTIONS.map((o) => {
        const selected = o.pref === pref;
        return (
          <button
            key={o.pref}
            ref={(el) => {
              if (el) refs.current.set(o.pref, el);
              else refs.current.delete(o.pref);
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={o.label}
            title={o.label}
            tabIndex={selected ? 0 : -1}
            className="cf-theme-toggle__seg"
            onClick={() => setPref(o.pref)}
          >
            <span className="cf-theme-toggle__icon" aria-hidden="true">
              {o.icon}
            </span>
          </button>
        );
      })}
    </div>
  );
}
